import { createSlice } from '@reduxjs/toolkit';
import {
  fetchAlumDoorTextures,
  fetchGlasses,
  fetchPriceTexture,
  fetchSpecialTextures,
  fetchTextureDetail,
  fetchTextureGroups,
  fetchTextures,
} from '../actions/texture.action';
import { roomPriceContextChanged } from '../actions/roomPriceContext.action';

const initialState = {
  glasses: {
    data: [],
    total: 0,
    preParams: {},
    isLoading: false,
    isNeedReloadData: true,
  },
  groupTextures: {
    data: [],
    total: 0,
    preParams: {},
    isLoading: false,
    isNeedReloadData: true,
  },
  textures: {
    data: [],
    total: 0,
    preParams: {},
    isLoading: false,
    isNeedReloadData: true,
  },
  specialTextures: {
    data: [],
    total: 0,
    preParams: {},
    isLoading: false,
    isNeedReloadData: true,
  },
  alumDoorTextures: {
    data: [],
    total: 0,
    preParams: {},
    isLoading: false,
    isNeedReloadData: true,
  },
  texture: {
    id: null,
    isLoading: false,
    isNeedReloadData: false,
  },
  priceTexture: {
    id: null,
    isLoading: false,
    isNeedReloadData: false,
    data: [],
  },
};

export const textureReducer = createSlice({
  name: 'Texture',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(roomPriceContextChanged, () => initialState)
      .addCase(fetchGlasses.pending, state => {
        state.glasses.isLoading = true;
        state.glasses.isNeedReloadData = false;
      })
      .addCase(fetchGlasses.fulfilled, (state, action) => {
        if (action.payload) {
          const { data: newData, total, offset } = action.payload;
          const params = action.meta.arg;
          const { data: oldData } = state.glasses;
          if (offset) {
            if (newData)
              state.glasses = {
                ...state.glasses,
                data: [...oldData, ...newData],
                total,
                preParams: { ...params },
                isLoading: false,
              };
          } else {
            state.glasses = {
              ...state.glasses,
              data: [...(newData ?? [])],
              total,
              preParams: { ...params },
              isLoading: false,
            };
          }
        }
      })
      .addCase(fetchAlumDoorTextures.pending, state => {
        state.alumDoorTextures.isLoading = true;
        state.alumDoorTextures.isNeedReloadData = false;
      })
      .addCase(fetchAlumDoorTextures.rejected, state => {
        state.alumDoorTextures.isLoading = false;
      })
      .addCase(fetchAlumDoorTextures.fulfilled, (state, action) => {
        if (action.payload) {
          const { data: newData, offset } = action.payload;
          const params = action.meta.arg;
          const { data: oldData } = state.alumDoorTextures;
          if (offset) {
            if (newData)
              state.alumDoorTextures = {
                ...state.alumDoorTextures,
                data: [...oldData, ...newData],
                preParams: { ...params },
                isLoading: false,
              };
          } else {
            state.alumDoorTextures = {
              ...state.alumDoorTextures,
              data: [...(newData ?? [])],
              preParams: { ...params },
              isLoading: false,
            };
          }
        }
      })
      .addCase(fetchSpecialTextures.pending, state => {
        state.specialTextures.isLoading = true;
        state.specialTextures.isNeedReloadData = false;
      })
      .addCase(fetchSpecialTextures.rejected, state => {
        state.specialTextures.isLoading = false;
      })
      .addCase(fetchSpecialTextures.fulfilled, (state, action) => {
        if (action.payload) {
          const { data: newData, total, offset } = action.payload;
          const params = action.meta.arg;
          const { data: oldData } = state.specialTextures;
          if (offset) {
            if (newData)
              state.specialTextures = {
                ...state.specialTextures,
                data: [...oldData, ...newData],
                total,
                preParams: { ...params },
                isLoading: false,
              };
          } else {
            state.specialTextures = {
              ...state.specialTextures,
              data: [...(newData ?? [])],
              total,
              preParams: { ...params },
              isLoading: false,
            };
          }
        }
      })
      .addCase(fetchTextureGroups.pending, state => {
        state.groupTextures.isLoading = true;
        state.groupTextures.isNeedReloadData = false;
      })
      .addCase(fetchTextureGroups.fulfilled, (state, action) => {
        if (action.payload) {
          const { data: newData, total, offset } = action.payload;
          const params = action.meta.arg;
          const { data: oldData } = state.groupTextures;
          if (offset) {
            if (newData)
              state.groupTextures = {
                ...state.groupTextures,
                data: [...oldData, ...newData],
                total,
                preParams: { ...params },
                isLoading: false,
              };
          } else {
            state.groupTextures = {
              ...state.groupTextures,
              data: [...(newData ?? [])],
              total,
              preParams: { ...params },
              isLoading: false,
            };
          }
        }
      })
      .addCase(fetchTextures.pending, state => {
        state.textures.isLoading = true;
        state.textures.isNeedReloadData = false;
      })
      .addCase(fetchTextures.fulfilled, (state, action) => {
        if (action.payload) {
          const { data: newData, total, offset } = action.payload;
          const params = action.meta.arg;
          const { data: oldData } = state.textures;
          if (offset) {
            if (newData)
              state.textures = {
                ...state.textures,
                data: [...oldData, ...newData],
                total,
                preParams: { ...params },
                isLoading: false,
              };
          } else {
            state.textures = {
              ...state.textures,
              data: [...(newData ?? [])],
              total,
              preParams: { ...params },
              isLoading: false,
            };
          }
        }
      })
      .addCase(fetchTextureDetail.pending, (state, action) => {
        state.texture.id = action.meta.arg?.id;
        state.texture.isLoading = true;
        state.texture.isNeedReloadData = false;
      })
      .addCase(fetchTextureDetail.fulfilled, state => {
        state.texture.id = null;
        state.texture.isLoading = false;
      })
      .addCase(fetchPriceTexture.pending, state => {
        state.priceTexture = {
          ...state.priceTexture,
          isLoading: true,
          isNeedReloadData: false,
        };
      })
      .addCase(fetchPriceTexture.fulfilled, (state, action) => {
        const { catalogId: id } = action.meta.arg;
        const { data } = action.payload;
        state.priceTexture = {
          ...state.priceTexture,
          isLoading: false,
          isNeedReloadData: false,
          data,
          id,
        };
      });
  },
});

export default textureReducer.reducer;
